import Vue from 'vue';
import COS from 'cos-js-sdk-v5';

/**
 * 腾讯云 COS 封装（STS 临时凭证 + 分片上传 + 对象下载）
 * 接口与 ossSTSService.js 保持完全一致，便于业务层无缝切换。
 *
 * 后端临时凭证接口：OSSParam/getStsCosParam
 * 返回结构：
 * tmpSecretId / tmpSecretKey / sessionToken、bucket、region、expiredTime、path、domain（自定义存储桶域名）
 */
export default class {
  constructor() {
    this.client = null;
    this.bucket = '';
    this.region = '';
    this.path = '';
    this.domain = ''; // 自定义存储桶域名
    this.token = null; // 缓存的 STS 凭证，避免重复请求
    this.errorObj = {
      AccessDenied: '无指定操作的权限',
      NoSuchBucket: '存储桶不存在',
      NoSuchKey: '文件不存在',
      RequestTimeout: '请求超时',
      SignatureDoesNotMatch: '签名不匹配',
      SignatureTimeout: '签名已过期',
      InvalidArgument: '参数错误',
      InvalidAccessKeyId: 'SecretId 不存在或已被禁用',
      RequestTimeTooSkewed: '发送请求的时间与 COS 收到请求的时间间隔超出了限制',
      UserDisable: '账号被禁用',
    };
  }

  /******************** STS 凭证相关 *********************************/
  /**
   * 获取 COS STS 临时凭证
   * @returns 标准化后的凭证对象
   * @throws 获取失败或关键字段缺失时抛出错误
   */
  async _requestSTSToken() {
    const res = await Vue.api.service.ossCommon_getStsCosToken();
    if (res && res.code === 0 && res.result) {
      const r = res.result;
      const tmpSecretId = r.tmpSecretId;
      const tmpSecretKey = r.tmpSecretKey;
      const sessionToken = r.sessionToken;
      const bucket = r.bucket;
      const region = r.region;
      const startTime = r.startTime;
      const expiredTime = r.expiredTime;
      const path = r.path;
      const domain = r.domain;
      if (!tmpSecretId || !bucket || !region || !domain) {
        throw new Error(res.msg || '获取COS上传凭证失败');
      }
      return { tmpSecretId, tmpSecretKey, sessionToken, bucket, region, startTime, expiredTime, path, domain };
    }
    throw new Error((res && res.msg) || '获取COS上传凭证失败');
  }
  /**
   * 获取 STS 凭证（有缓存时直接复用）
   * 不使用客户端时间判断过期：客户端时钟可能不准，凭证失效交给请求报错后的强制刷新兜底
   * @param force 为 true 时忽略缓存，强制重新获取
   */
  async _getCredential({ force = false } = {}) {
    if (!force && this.token) {
      return this.token;
    }
    this.token = null;
    const token = await this._requestSTSToken();
    this.token = token;
    this.bucket = token.bucket;
    this.region = token.region;
    this.path = token.path;
    this.domain = token.domain;
    return token;
  }
  /**
   * 确保 COS client 可用（幂等）：已就绪时直接复用，未就绪或 force 时重新初始化
   * @param force 为 true 时强制刷新凭证并重建 client（用于凭证失效后的重试）
   */
  async _ensureCOS({ force = false } = {}) {
    // 已就绪且不强制刷新时直接复用，避免重复初始化；凭证失效交给请求报错后的强制刷新兜底
    if (!force && this.client && this.bucket && this.region) {
      return;
    }
    if (force) {
      this.client = null;
    }
    // 提前把 Bucket/Region/Path/Domain 填好，uploadFile/getObject 同步读取时不会为空
    await this._getCredential({ force });
    if (!this.client) {
      // 通过 getAuthorization 让 SDK 在凭证过期时自动刷新，无需手动续期
      this.client = new COS({
        getAuthorization: async (_options, callback) => {
          try {
            const token = await this._getCredential();
            callback({
              TmpSecretId: token.tmpSecretId,
              TmpSecretKey: token.tmpSecretKey,
              SecurityToken: token.sessionToken,
              StartTime: token.startTime, // 时间戳，单位秒
              ExpiredTime: token.expiredTime, // 时间戳，单位秒
              ScopeLimit: false, // 细粒度控制权限需要设为 true，会限制密钥只在相同请求时重复使用
            });
          } catch (error) {
            // 触发 SDK 抛错，由上传/下载的重试逻辑兜底
            console.error('获取COS上传凭证失败:', error);
            callback({});
          }
        },
      });
    }
  }

  /******************** ERROR处理 *********************************/
  /**
   * 仅当错误属于 STS 临时凭证过期/失效时才允许重试，避免对网络错误、参数错误等其他错误做无效重试
   */
  _isSTSTokenExpiredError(err) {
    if (!err) return false;
    const detail = err.error;
    const status = err.statusCode;
    const code = (detail && detail.Code) || err.code || err.Code || err.name || '';
    const message = `${(detail && detail.Message) || ''} ${err.message || ''}`.toLowerCase();
    const isAuthStatus = status === 403 || status === 401;
    const isCredentialCode = /AccessDenied|InvalidAccessKeyId|SignatureDoesNotMatch|SignatureTimeout/i.test(code);
    const isExpiredMsg = /token|expired|secretid|security ?token|accesskey/i.test(message);
    return isAuthStatus && (isCredentialCode || isExpiredMsg);
  }
  /**
   * 识别"用户取消"错误：取消后不重试、不提示失败。COS 取消后错误码不固定，做宽松匹配兜底
   */
  _isUploadCanceledError(err) {
    if (!err) return false;
    const code = err.error?.Code || err.code || err.Code || err.name || '';
    const message = `${err.error?.Message || ''} ${err.message || ''}`;
    return /UserCanceled|UserCancelled|Canceled|Cancelled/i.test(code) || /cancel/i.test(message);
  }
  /**
   * 将 COS 错误对象转换为可读的提示信息
   */
  _getErrorMsg(err) {
    if (!err) return 'COS操作失败';
    const detail = err.error;
    const code = (detail && detail.Code) || err.code || err.Code || err.name;
    return this.errorObj[code] || (detail && detail.Message) || err.message || code || 'COS操作失败';
  }

  /******************** COS方法 *********************************/
  /**
   * 获取对象的签名 URL（内部方法，把 COS 回调封装为 Promise，并防御 data 为空）
   * @param key 对象 Key
   * @returns 签名后的 URL
   */
  _cosGetObjectUrl({ key }) {
    return new Promise((resolve, reject) => {
      this.client.getObjectUrl(
        {
          Bucket: this.bucket,
          Region: this.region,
          Key: key,
          Sign: true,
          Expires: 3600, // 签名有效期（秒）
        },
        (err, data) => {
          // 出错或拿不到有效 Url 都按失败处理，不能用 { code: 0 } 掩盖错误
          if (err) {
            reject(err);
            return;
          }
          if (!data || !data.Url) {
            reject(new Error('获取COS文件地址失败'));
            return;
          }
          resolve(data.Url);
        }
      );
    });
  }
  /**
   * 获取对象的签名 URL，STS 凭证过期/失效时刷新凭证后仅重试 1 次
   * 成功返回 { code: 0, data }，失败返回 { code: 1, msg }，与 _cosUploadFile 结构保持一致
   */
  async cosGetSignedUrl({ key }, retried = false) {
    try {
      // 补一层保障，避免方法被单独调用时 client 尚未初始化而抛 TypeError
      await this._ensureCOS();
      const url = await this._cosGetObjectUrl({ key });
      return { code: 0, data: url };
    } catch (err) {
      // 仅 STS 凭证过期/失效时刷新凭证重试 1 次；其他错误或重试后仍失败直接返回错误
      if (!retried && this._isSTSTokenExpiredError(err)) {
        try {
          // 凭证失效必须强制刷新并重建 client，否则会拿到缓存里同样的过期凭证
          await this._ensureCOS({ force: true });
        } catch (initError) {
          // 刷新凭证失败必须正常返回，否则 Promise 悬挂会让调用方一直 await 且无响应
          return { code: 1, msg: this._getErrorMsg(initError) };
        }
        return this.cosGetSignedUrl({ key }, true);
      }
      return { code: 1, msg: this._getErrorMsg(err) };
    }
  }
  /**
   * 上传文件（STS 凭证过期/失效时刷新凭证后仅重试 1 次，仍失败说明是权限或配置问题）
   * @param file 二进制内容（Blob / ArrayBuffer / File）
   * @param options { onProgress(percent), onTaskReady(taskId) }
   * @param retried 是否已重试过，避免凭证失效时无限重试
   * @param cosKey 逻辑任务的对象 Key，首次为空时生成，凭证失效重试时复用
   */
  async _cosUploadFile(file, { onProgress, onTaskReady } = {}, cosKey = '', retried = false) {
    // 同一次上传逻辑只生成一次 Key，重试复用，避免在桶里留下孤儿对象
    let uploadKey = cosKey;
    try {
      await this._ensureCOS();
      if (!uploadKey) {
        const lastPath = `${moment().format('YYYYMMDDHHmmssSSS')}${Math.round(Math.random() * 100)}`;
        const type = typeof file.type === 'string' ? file.type : '';
        const ext = type.split('/')[1] || 'jpg';
        const fileName = file.name || `${lastPath}.${ext}`;
        uploadKey = `${this.path}/${lastPath}/${fileName}`;
      }
      await this.client.uploadFile({
        Bucket: this.bucket,
        Region: this.region,
        Key: uploadKey,
        Body: file,
        SliceSize: 1024 * 1024 * 5, // 5MB 分片上传
        onTaskReady: taskId => {
          onTaskReady?.(taskId);
        },
        onProgress: ({ percent }) => {
          onProgress?.(percent);
        },
      });
      const fileName = uploadKey.substring(uploadKey.lastIndexOf('/') + 1);
      const fileUrl = `${this.domain}/${uploadKey}`;
      return { code: 0, url: fileUrl, msg: '上传成功', fileName };
    } catch (err) {
      // 取消优先：用户主动取消不重试、不按失败处理
      if (this._isUploadCanceledError(err)) {
        return { code: 2, msg: '上传已取消' };
      }
      // 仅 STS 凭证过期/失效时刷新凭证重试 1 次；其他错误或重试后仍失败直接返回
      if (!retried && this._isSTSTokenExpiredError(err)) {
        try {
          // 凭证失效必须强制刷新并重建 client，否则会拿到缓存里同样的过期凭证
          await this._ensureCOS({ force: true });
        } catch (initError) {
          return { code: 1, msg: this._getErrorMsg(initError) };
        }
        // 先 await 再 return，保证重试流程完整结束；uploadKey 一并传入，重试复用同一 Key
        const retriedResult = await this._cosUploadFile(file, { onProgress, onTaskReady }, uploadKey, true);
        return retriedResult;
      }
      return { code: 1, msg: this._getErrorMsg(err) };
    }
  }
  /**
   * 上传文件的公共逻辑
   * @param file 二进制内容（Blob / ArrayBuffer / File）
   * @param options 可选项 { onProgress(percent), onTaskReady(taskId) }
   * @returns 返回 { code, msg, url, fileName }，取消时 code 为 2
   */
  async uploadFile(file, options = {}) {
    const result = { code: 1, msg: '上传失败', url: '', fileName: '' };
    if (file) {
      const res = await this._cosUploadFile(file, options, '', false);
      if (res.code === 1) {
        Vue.api.element.toast(res.msg, 'error');
      }
      return { ...result, ...res };
    } else {
      Vue.api.element.toast('请选择文件', 'error');
      return result;
    }
  }
  /**
   * 取消指定的 COS 上传任务
   * @param taskId onTaskReady 回调中拿到的任务 ID
   * @returns 是否成功发起取消
   */
  async cosCancelUpload(taskId) {
    if (!taskId) {
      return false;
    }
    try {
      await this._ensureCOS();
      this.client.cancelTask(taskId);
      return true;
    } catch (error) {
      console.error('取消COS上传任务失败:', error);
      return false;
    }
  }
  /**
   * 下载 COS 文件
   * @param file 完整文件 URL 或 objectKey
   */
  async cosDownloadFile(file) {
    if (!file) return Promise.reject('文件地址不能为空');
    // 兼容已编码/未编码的地址：解码失败时按原始值处理，避免 URIError 直接抛出
    let fileUrl = file;
    try {
      fileUrl = decodeURIComponent(file);
    } catch (e) {
      console.log('文件地址解码失败，使用原始值:', e);
      fileUrl = file;
    }
    const fileName = fileUrl.substring(fileUrl.lastIndexOf('/') + 1);
    // 兼容完整 URL 与 objectKey 两种入参
    let key = fileUrl;
    try {
      const u = new URL(fileUrl);
      key = u.pathname.replace(/^\/+/, '');
    } catch (e) {
      // 不是 URL，直接使用 objectKey
      console.log('不是 URL，直接使用 objectKey:', e);
      key = fileUrl;
    }
    // pathname 可能仍处于编码状态，单独解码；失败时保留原值，避免误判为「不是 URL」
    try {
      key = decodeURIComponent(key);
    } catch (e) {
      console.warn('objectKey 解码失败，使用原始值:', e);
    }
    try {
      const res = await this.cosGetSignedUrl({ key });
      if (res.code !== 0) {
        return Promise.reject(res.msg);
      }
      // 补充强制下载的参数
      const downloadUrl = res.data + (res.data.indexOf('?') > -1 ? '&' : '?') + 'response-content-disposition=attachment';
      const aDownload = document.createElement('a');
      aDownload.href = downloadUrl;
      aDownload.download = fileName;
      aDownload.style.display = 'none';
      document.body.appendChild(aDownload);
      aDownload.click();
      requestAnimationFrame(() => {
        document.body.removeChild(aDownload);
      });
      return { code: 0, msg: '下载成功' };
    } catch (e) {
      console.error('文件下载失败:', e);
      return Promise.reject('文件下载失败');
    }
  }
}
